import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Music, Zap, TreePine, GraduationCap, BadgeCheck, Hash } from "lucide-react";
import { Reveal, ChapterHeader } from "./Reveal";
import { IMAGES, CONTACT } from "@/lib/scroll";

const CREDENTIALS = [
  { icon: GraduationCap, text: "MA (Counselling Psychology), Cum Laude, University of the Witwatersrand" },
  { icon: BadgeCheck, text: `HPCSA Registration: ${CONTACT.hpcsa}` },
  { icon: Hash, text: `Practice Number: ${CONTACT.practiceNo}` },
];

const PERSONAL = [
  { icon: Music, label: "Making music" },
  { icon: Zap, label: "Kickboxing" },
  { icon: TreePine, label: "Time in nature" },
];

export const About = () => {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-30, 30]);

  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-32 bg-pawc-sand">
      <div className="max-w-6xl mx-auto px-6">
        <ChapterHeader number="01" eyebrow="About" title="A grounded, human practice" />

        <div className="grid md:grid-cols-12 gap-12 md:gap-16 mt-12 items-start">
          <Reveal className="md:col-span-5 mb-16 md:mb-0" delay={0.1}>
            <div ref={imgRef} className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full rounded-3xl border border-pawc-terra/40" aria-hidden="true" />
              <div className="relative rounded-3xl overflow-hidden shadow-[0_25px_50px_-12px_rgba(14,95,115,0.15)]">
                <motion.img
                  style={{ y }}
                  src={IMAGES.portrait}
                  alt="Anoshamisa Clive Benhura in a light blue suit, standing in a garden"
                  className="w-full h-[420px] md:h-[520px] object-cover object-[center_22%] scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-pawc-deep/40 to-transparent" />
              </div>
              <motion.div
                initial={{ opacity: 0, y: 24, rotate: 4 }}
                whileInView={{ opacity: 1, y: 0, rotate: 2 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="absolute -bottom-10 -right-3 md:-right-8 w-36 md:w-48 rounded-2xl overflow-hidden border-4 border-pawc-sand shadow-[0_20px_40px_-12px_rgba(8,54,70,0.35)]"
              >
                <img
                  src={IMAGES.portraitGarden}
                  alt="Anoshamisa in a lush garden, wearing a woven hat"
                  className="w-full h-44 md:h-60 object-cover object-[center_30%]"
                />
              </motion.div>
            </div>
          </Reveal>

          <div className="md:col-span-7 max-w-[65ch]">
            <Reveal delay={0.15}>
              <h3 className="font-serif text-2xl md:text-3xl text-pawc-teal mb-6">
                {CONTACT.name}
              </h3>
              <div className="space-y-5 text-pawc-slate text-base md:text-lg leading-relaxed">
                <p>
                  I am an HPCSA-registered Counselling Psychologist with a Master's
                  degree cum laude from Wits University. I work with young people
                  and adults navigating trauma, emotional distress and life
                  transitions. I am passionate about early psychological support, as
                  I believe it helps build strong foundations for lifelong
                  wellbeing.
                </p>
                <p>
                  I see therapy as a respectful journey of rediscovering strengths,
                  meaning and wellbeing.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.25}>
              <ul className="mt-10 space-y-4">
                {CREDENTIALS.map((c) => (
                  <li key={c.text} className="flex items-start gap-4">
                    <span className="mt-1 text-pawc-terra">
                      <c.icon size={18} strokeWidth={1.5} />
                    </span>
                    <span className="text-sm md:text-base text-pawc-ink">{c.text}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={0.35}>
              <div className="mt-10 rounded-2xl bg-pawc-paper border border-pawc-teal/10 p-6 md:p-8">
                <p className="text-xs tracking-[0.25em] uppercase text-pawc-teal mb-4">
                  Beyond the therapy room
                </p>
                <p className="text-pawc-slate text-sm md:text-base leading-relaxed mb-5">
                  Outside of work, I enjoy making music, kickboxing and being
                  near water, the same stillness and rhythm I hope to help you
                  find.
                </p>
                <div className="flex flex-wrap gap-3">
                  {PERSONAL.map((p) => (
                    <span
                      key={p.label}
                      className="inline-flex items-center gap-2 rounded-full border border-pawc-teal/20 bg-pawc-sand px-4 py-2 text-xs text-pawc-deep"
                    >
                      <p.icon size={14} strokeWidth={1.5} className="text-pawc-terra" />
                      {p.label}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>

          </div>
        </div>
      </div>
    </section>
  );
};
